import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import {
  NavigationBreadcrumbExpansion,
  NavigationBreadcrumbOption,
  NavigationBreadcrumbPathStep,
  NavigationBreadcrumbSelection,
} from './navigation-breadcrumb.model';

/** Filters options by a search term (case-insensitive). Shared with the breadcrumb's specs. */
export function filterBreadcrumbOptions<T extends { name: string }>(
  options: T[] | null | undefined,
  term: string
): T[] {
  if (!options) return [];
  if (!term) return options;
  const t = term.toLowerCase();
  return options.filter((option) => option.name?.toLowerCase().includes(t));
}

/**
 * One dropdown panel of a breadcrumb level, and — recursively — of every option in it
 * that expands into a deeper level.
 *
 * It is a component rather than a recursive `<ng-template>` on purpose: Angular content
 * queries match by DECLARATION site, so options outlet-ed from a template declared
 * elsewhere are invisible to the `<mat-menu>` they render into. `MatMenu` needs to see its
 * items as content children — that is how it drives `hovered()`, which is what opens a
 * submenu. Declaring one component per panel keeps every item lexically inside its own
 * `<mat-menu>`. For the same reason the content is NOT wrapped in `matMenuContent`: lazy
 * content is instantiated in a portal, outside the query.
 *
 * The host element renders nothing (the panel is portaled to the overlay on open), so it
 * is hidden — otherwise it would take part in its parent's layout.
 */
@Component({
  selector: 'ts-navigation-breadcrumb-menu',
  templateUrl: './navigation-breadcrumb-menu.component.html',
  styles: [':host { display: none; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatMenuModule,
    MatTooltipModule,
    // Self-reference for the recursion; forwardRef because the class is not defined yet.
    forwardRef(() => NavigationBreadcrumbMenuComponent),
  ],
})
export class NavigationBreadcrumbMenuComponent {
  /** Options of this panel. `null`/`undefined` renders a loading row. */
  @Input() options?: NavigationBreadcrumbOption[] | null;
  /** Class list for this panel and every panel below it — each portals separately. */
  @Input() panelClass = 'breadcrumb-menu';
  /** The chain of options chosen above this panel, deepest last. */
  @Input() path: NavigationBreadcrumbPathStep[] = [];
  /** Ids of the currently-open entities, used to tick the active row. */
  @Input() activeIds: readonly string[] = [];
  /** Level for options that don't name their own — only set for a level's own panel. */
  @Input() fallbackLevelKey: string | null = null;
  /** Translation key shown when `options` is loaded but empty. */
  @Input() emptyKey: string | null = null;

  @Output() optionSelected = new EventEmitter<NavigationBreadcrumbSelection>();
  @Output() optionExpanded = new EventEmitter<NavigationBreadcrumbExpansion>();

  /** Static: the parent binds `[matMenuTriggerFor]="child.menu"` in its first update pass. */
  @ViewChild('menu', { static: true }) public menu!: MatMenu;

  public search = '';

  public resetSearch(): void {
    this.search = '';
  }

  public get visibleOptions(): NavigationBreadcrumbOption[] {
    return filterBreadcrumbOptions(this.options, this.search);
  }

  public trackByOptionId(_index: number, option: NavigationBreadcrumbOption): string {
    return option.id;
  }

  /** A child option carries its own level; a top-level one inherits the level it is listed under. */
  public levelKeyOf(option: NavigationBreadcrumbOption): string {
    return option.levelKey ?? this.fallbackLevelKey ?? '';
  }

  public isActive(option: NavigationBreadcrumbOption): boolean {
    return !!option.id && this.activeIds.includes(option.id);
  }

  public pathWith(option: NavigationBreadcrumbOption): NavigationBreadcrumbPathStep[] {
    return [...this.path, { levelKey: this.levelKeyOf(option), optionId: option.id }];
  }
}
